'use client';

import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { showModal, toggleSelectedMediaItems } from '@/store/reducers/ui-slice';
import { OperationResult } from '@alien-platform/bo-sdk';
import { Media } from '@alien-platform/cms-bo';
import { Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { CloudArrowUp as CloudArrowUpIcon } from '@phosphor-icons/react/dist/ssr/CloudArrowUp';
import { Trash as TrashIcon } from '@phosphor-icons/react/dist/ssr/Trash';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { useDispatch } from 'react-redux';

import { ModalDrawer } from '@/types/general';
import { bytesToSize, fileToBase64 } from '@/lib/helpers';
import { useMediaQuery } from '@/hooks/use-media-query';
import { toast } from '@/components/core/toaster';

function UploadArea({
  customKey,
  id,
  dragOver,
  onDragOver,
  onDrop,
  onDragLeave,
  onFileSelect,
  recommendedDimensions,
}: {
  customKey?: string;
  id: number;
  onDragOver?: React.DragEventHandler<HTMLDivElement> | undefined;
  dragOver: boolean;
  onDrop?: React.DragEventHandler<HTMLDivElement> | undefined;
  onDragLeave?: React.DragEventHandler<HTMLDivElement> | undefined;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  recommendedDimensions?: string;
}): React.JSX.Element {
  const dispatch = useDispatch();
  const mdDown = useMediaQuery('down', 'md');

  return (
    <Stack>
      <Stack
        direction="row"
        gap={1.5}
        sx={{
          position: 'relative',
          justifyContent: 'center',
          alignItems: 'center',
          border: `2px dashed ${dragOver ? 'var(--mui-palette-primary-500)' : 'var(--mui-palette-divider)'}`,
          backgroundColor: dragOver ? 'var(--mui-palette-background-level1)' : 'transparent',
          height: '112px',
          borderRadius: '8px',
          cursor: 'pointer',
        }}
        onClick={() => document.getElementById(`fileInput-${id}`)?.click()}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragLeave={onDragLeave}
      >
        <input id={`fileInput-${id}`} type="file" accept="image/*" hidden onChange={onFileSelect} />
        <Box
          sx={{
            width: '58px',
            height: '58px',
            p: 1.5,
            background: 'white',
            borderRadius: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CloudArrowUpIcon fontSize="32" color="var(--mui-palette-text-secondary)" />
        </Box>
        <Stack sx={{ textAlign: 'left' }}>
          <Typography color="text.secondary">
            {mdDown ? 'Click to Upload' : dragOver ? 'Drop the file here' : 'Click to upload or drag and drop'}
          </Typography>
        </Stack>
      </Stack>
      <Stack sx={{ my: 1 }}>
        <Button
          variant="outlined"
          sx={{
            background: 'var(--mui-palette-background-paper)',
            color: 'var(--mui-palette-text-secondary)',
            borderColor: 'var(--mui-palette-text-secondary)',
            '&:hover': {
              borderColor: 'var(--mui-palette-text-secondary)',
            },
          }}
          onClick={() => {
            dispatch(
              showModal({
                open: ModalDrawer.MEDIA_PICKER,
                content: {
                  target: {
                    customKey,
                    index: id,
                  },
                },
              })
            );
          }}
        >
          Choose From Library
        </Button>
      </Stack>
      <Box sx={{ width: 1, height: '40px', px: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          Max file size is 2MB {recommendedDimensions ? `and ${recommendedDimensions} px` : ''}
        </Typography>
      </Box>
    </Stack>
  );
}

function FilePreview({ uploadPreview }: { uploadPreview: string | undefined }): React.JSX.Element {
  const [preview, setPreview] = useState(uploadPreview);
  const mdDown = useMediaQuery('down', 'md');

  return (
    <img
      src={preview === '' ? '/assets/not-found.svg' : preview}
      alt="for blanks"
      style={{
        height: '100%',
        width: preview === '' ? 60 : '100%',
        maxHeight: mdDown ? '180px' : '160px',
        objectFit: 'contain',
        paddingTop: mdDown ? '16px' : 0,
      }}
      onError={() => {
        setPreview('');
      }}
    />
  );
}
function FileDetails({
  file,
  fileSize,
  onDelete,
}: {
  file: File | null;
  fileSize: number | null;
  onDelete: () => void;
}): React.JSX.Element {
  const mdDown = useMediaQuery('down', 'md');
  return (
    <Stack direction="row" sx={{ height: 1, justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
      <Box sx={{ p: 1 }}>
        {/* {preview === '' ? (
          <Typography
            variant="body2"
            color="error"
            sx={{ maxWidth: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            (File is corrupted!)
          </Typography>
        ) : null} */}
        <Typography
          variant="body2"
          sx={{
            maxWidth: mdDown ? '140px' : '250px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {file?.name}
        </Typography>
        {fileSize ? (
          <Typography variant="body2" color="text.secondary">
            Size is {bytesToSize(fileSize)}
          </Typography>
        ) : null}
        {/* {dimensions?.width && dimensions?.height ? (
          <Typography color="text.secondary" variant="body2">
            {dimensions?.width}x{dimensions?.height} px
          </Typography>
        ) : null} */}

        {/* {recommendedDimensions ? (
          <Typography variant="body2" color="text.secondary">
            Max file size is 2MB and {recommendedDimensions}
          </Typography>
        ) : null} */}
      </Box>
      <IconButton onClick={onDelete}>
        <TrashIcon color="var(--mui-palette-action-active)" />
      </IconButton>
    </Stack>
  );
}

export function UploadCard({
  id,
  image,
  width,
  onUpload,
  handleDelete,
  customKey,
  recommendedDimensions,
}: {
  id: number;
  image?: { url: string; alt?: string };
  width?: string | number | undefined;
  onUpload?: (file: File) => void;
  handleDelete?: (id: number | string) => void;
  customKey?: string;
  recommendedDimensions?: string;
}): React.JSX.Element {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const allowedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  const [preview, setPreview] = useState<string | undefined>(undefined);
  const [fileSize, setFileSize] = useState<number | null>(null);
  // const [passedImage, setPassedImage] = useState(image);
  const searchParams = useSearchParams();
  const selectedOpId = searchParams.get('opId');
  const dispatch = useDispatch();
  const mdDown = useMediaQuery('down', 'md');

  const imageFullUrl = image?.url ? `${process.env.NEXT_PUBLIC_CMS_V2_IMAGES_URL}${image?.url}` : undefined;

  // useEffect(() => {
  //   setPassedImage(image);
  // }, [image]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      // handleUpload(file);
      validateAndUpload(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      // handleUpload(file);
      validateAndUpload(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (): void => {
    setDragOver(false);
  };
  const onDelete = (): void => {
    setSelectedFile(null);
    setPreview(undefined);
    setFileSize(null);
    // setPassedImage(undefined);

    dispatch(
      toggleSelectedMediaItems({
        media: undefined,
        target: {
          customKey,
          index: id,
        },
      })
    );
  };

  const handleUpload = useCallback(
    async (file: File): Promise<void> => {
      if (!file) return;
      try {
        const base64String = await fileToBase64(file);
        const res = await fetch(`/api/cms/gallery/upload?opId=${selectedOpId}`, {
          method: 'POST',
          body: JSON.stringify({ file: base64String, filename: file.name }),
        });
        const result: OperationResult<Media> = await res.json();

        if (result.success && result.data) {
          // const finalPath = `${process.env.NEXT_PUBLIC_CMS_IMAGES_URL}${result.data.path}`;
          setPreview(`${process.env.NEXT_PUBLIC_CMS_IMAGES_URL}${result.data.path}`);
          dispatch(toggleSelectedMediaItems({ media: result.data, target: { customKey, index: id } }));
          toast.success('Media uploaded successfully!');
        }
      } catch {
        toast.error('Error uploading file. Try again later.');
      }
    },
    [dispatch, selectedOpId, customKey, id]
  );

  const validateAndUpload = (file: File): void => {
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size exceeds the 2 MB limit.');
      return;
    }
    if (!allowedFormats.includes(file.type)) {
      toast.error('Invalid file format! Only JPG, PNG, GIF, WEBP, and SVG are allowed.');
      return;
    }

    const sizeInMB = file.size / (1024 * 1024);
    setFileSize(parseFloat(sizeInMB.toFixed(2)));

    const reader = new FileReader();
    reader.onloadend = () => {
      const imageUrl = reader.result as string;

      setPreview(reader.result as string);

      const img = new Image();
      // img.onload = () => {
      //   setDimensions({ width: Math.round(img.width), height: Math.round(img.height) });
      // };
      img.src = imageUrl;
    };
    reader.readAsDataURL(file);

    setSelectedFile(file);

    onUpload?.(file);

    void handleUpload(file);
  };

  return (
    <Box
      sx={{
        overflow: 'hidden',
        borderRadius: '8px',
        border: '1px solid var(--mui-palette-divider)',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        width: width || 'auto',
      }}
    >
      <Table sx={{ borderCollapse: 'separate', borderRadius: '8px' }}>
        <TableHead>
          {mdDown ? (
            <TableRow>
              <TableCell
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>Image</span>
                {handleDelete ? (
                  <Box
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedFile(null);
                      setPreview(undefined);

                      handleDelete(id);
                    }}
                  >
                    <X size={16} />
                  </Box>
                ) : null}
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>Details</span>
                {handleDelete ? (
                  <Box
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedFile(null);
                      setPreview(undefined);

                      handleDelete(id);
                    }}
                  >
                    <X size={16} />
                  </Box>
                ) : null}
              </TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {(!image?.url || image?.url === '' || image?.url === 'undefined') && !preview ? (
            <TableRow>
              <TableCell colSpan={2} sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                <UploadArea
                  customKey={customKey}
                  id={id}
                  dragOver={dragOver}
                  onDrop={handleDrop}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onFileSelect={handleFileSelect}
                  recommendedDimensions={recommendedDimensions}
                />
              </TableCell>
            </TableRow>
          ) : mdDown ? (
            <TableRow>
              <TableCell sx={{ minWidth: '250px', display: 'flex', flexDirection: 'column' }}>
                <FilePreview uploadPreview={preview || imageFullUrl} />
                <FileDetails onDelete={onDelete} file={selectedFile} fileSize={fileSize} />
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell sx={{ minWidth: '250px', height: 140 }}>
                <FilePreview uploadPreview={preview || imageFullUrl} />
              </TableCell>
              <TableCell colSpan={handleDelete ? 3 : 2} sx={{ width: '100%' }}>
                <FileDetails onDelete={onDelete} file={selectedFile} fileSize={fileSize} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
